/**
 * Tenant context — sourced from URL subdomain or login response.
 * Multi-tenant isolation enforced server-side; this is for client UX only.
 */
// localStorage keys (not secrets) — actual auth token in HTTP-only cookie server-side
const TOKEN_KEY = "dikdoc.auth.token";
const TENANT_KEY = "dikdoc.tenant.id";
const USER_KEY = "dikdoc.user";

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  facilities: string[];
  specialties: string[];
  abacAttributes: Record<string, string | number | boolean | string[]>;
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token === null) window.localStorage.removeItem(TOKEN_KEY);
  else window.localStorage.setItem(TOKEN_KEY, token);
}

export function getTenantId(): string | null {
  if (typeof window === "undefined") return null;
  // 1. From subdomain (e.g. "medigroup" from medigroup.dikdoc.io)
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length > 2 && parts[0] !== "www") return parts[0];
  // 2. From localStorage (after login)
  return window.localStorage.getItem(TENANT_KEY);
}

export function setTenantId(id: string): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(TENANT_KEY, id);
}

export function getCurrentUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function setCurrentUser(user: AuthUser | null): void {
  if (typeof window === "undefined") return;
  if (user === null) window.localStorage.removeItem(USER_KEY);
  else window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}


// ========== React Context Provider ==========
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

const TenantContext = createContext<{
  user: AuthUser | null;
  token: string | null;
  tenantId: string | null;
  setAuth: (token: string, user: AuthUser) => void;
  clear: () => void;
}>({
  user: null,
  token: null,
  tenantId: null,
  setAuth: () => {},
  clear: () => {},
});

export function TenantContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getCurrentUser());
  const [token, setToken] = useState<string | null>(getAuthToken());
  const [tenantId, setTenantId] = useState<string | null>(getTenantId());

  // Cross-tab sync
  useEffect(() => {
    const handler = () => {
      setUser(getCurrentUser());
      setToken(getAuthToken());
      setTenantId(getTenantId());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <TenantContext.Provider
      value={{
        user,
        token,
        tenantId,
        setAuth: (newToken, newUser) => {
          setAuthToken(newToken);
          setCurrentUser(newUser);
          setUser(newUser);
          setToken(newToken);
        },
        clear: () => {
          setAuthToken(null);
          setCurrentUser(null);
          setUser(null);
          setToken(null);
        },
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
